import { type ComponentProps, type ReactNode } from "react";

import {
  CaretRightIcon,
  ChevronDownIcon,
  Button,
  Divider,
  SearchField,
  SingleSelectDropdown,
  SingleSelectDropdownItem,
  capitalize,
  cn,
} from "@stacklet/ui";
import pluralize from "pluralize";

import { XGridTable } from "./XGridTable";

/** Default selectable page sizes for the pagination footer */
/** The page sizes the pagination footer offers unless a caller overrides them. */
export const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100] as const;

// The action bar, grid and pagination footer stack into one bordered block, as
// the design system's "Datagrid Action Bar" / "Data Grid" / "Pagination" trio
// does. The bar lives inside the grid root, which draws the block's outer box,
// so the bar contributes only the heavier rule under itself — and no outer
// margin, so it sits flush on the rows.
const ACTION_BAR_CLASSES = cn(
  "mb-0 min-h-12 rounded-none",
  "border-b-2 border-overlay-medium bg-background-secondary",
  // The search field brings its own padding; the bar adds just enough of its
  // own to keep the field — and its focus ring — clear of the border.
  "p-0 py-3xs pl-xs pr-xs",
);

// The outer box goes on the grid *root*: MUI measures the root and sizes the
// scroller viewport inside it, whereas a border on an inner element (.main,
// say) narrows the viewport without narrowing the columns, leaving every grid
// with a couple of pixels of phantom horizontal scroll. Column rules come from
// MUI's own vertical-border props (below); this recolours them — and the row
// rules MUI already draws — to the design's token, over MUI's own
// `withBorderColor` rule.
const GRID_CHROME_CLASSES = cn(
  "mt-0",
  "[&.MuiDataGrid-root]:rounded-none [&.MuiDataGrid-root]:rounded-t-xs",
  // `border-solid` is not redundant: the base grid turns the root's box off
  // with `border-none`, which is a border-*style*, so a width alone won't
  // bring it back.
  "[&.MuiDataGrid-root]:border [&.MuiDataGrid-root]:border-solid [&.MuiDataGrid-root]:border-overlay-medium",
  // v7 dropped `withBorderColor` from the cells, so their rules come off the
  // grid's own variable instead. Set both to keep every rule one colour.
  "[&_.MuiDataGrid-withBorderColor]:!border-overlay-medium",
  "[&.MuiDataGrid-root]:[--DataGrid-rowBorderColor:rgba(var(--overlay-medium))]",
  // …except the last row, whose rule MUI hides so it doesn't double up with the
  // box's own bottom edge. Recolouring `withBorderColor` would bring it back.
  "[&_.MuiDataGrid-row--lastVisible_.MuiDataGrid-cell]:!border-b-transparent",
  // MUI puts a right rule on *every* cell and header, last column included,
  // which doubles up against the box's own right edge. Drop that one. It has
  // to be matched as the last element *carrying the rule* rather than the last
  // child: a row whose columns don't fill the width gets a trailing filler
  // cell, which would take `:last-child` and leave the real one bordered.
  "[&_.MuiDataGrid-cell--withRightBorder:not(:has(~.MuiDataGrid-cell--withRightBorder))]:!border-r-0",
  "[&_.MuiDataGrid-columnHeader--withRightBorder:not(:has(~.MuiDataGrid-columnHeader--withRightBorder))]:!border-r-0",
);

/**
 * Cursor-pagination state and handlers for the footer. Supplying this prop
 * renders the footer; omitting it hides pagination entirely (e.g. for a
 * row-grouped grid that shows every row at once).
 */
export interface StackletGridPagination {
  /** Current page size */
  pageSize: number;
  /** Selectable page sizes; defaults to 5/10/25/50/100 */
  pageSizeOptions?: readonly number[];
  /** Called when a new page size is chosen */
  onPageSizeChange: (size: number) => void;
  /** Whether a next page exists */
  hasNextPage: boolean;
  /** Whether a previous page exists */
  hasPreviousPage: boolean;
  /** Advance to the next page */
  onNextPage: () => void;
  /** Return to the previous page */
  onPreviousPage: () => void;
  /** 1-based index of the first row on the current page */
  rangeStart: number;
  /** 1-based index of the last row on the current page */
  rangeEnd: number;
  /** Total number of rows across all pages, when known */
  total?: number | null;
  /** Singular noun for the summary text, e.g. "user"; defaults to "item" */
  unit?: string;
  /** Disables the page controls (e.g. while a page is loading) */
  isDisabled?: boolean;
}

type XGridTableProps = ComponentProps<typeof XGridTable>;

/** Props for the StackletGrid component */
interface Props extends Omit<XGridTableProps, "customInline" | "pagination"> {
  /** Controlled value of the search field; omit to hide the search field */
  search?: string;
  /** Called on each search keystroke; required to show the search field */
  onSearchChange?: (value: string) => void;
  /** Placeholder text for the search field */
  searchPlaceholder?: string;
  /** Accessible label for the search field */
  searchAriaLabel?: string;
  /** Pagination state; supply to render the footer, omit to hide it */
  pagination?: StackletGridPagination;
  /** Escape hatch to render arbitrary content in the toolbar instead of search */
  customInline?: ReactNode;
  /** Drops the search field's box (bg + border) so it blends into the toolbar. */
  transparentSearch?: boolean;
}

/**
 * Shared browser-style data grid built on {@link XGridTable}. Owns the chrome
 * common to Stacklet's list views — a search field and export button in the
 * toolbar, and an optional external cursor-pagination footer — while leaving
 * data fetching to the caller. Any DataGrid prop (columns, rows, loading, row
 * grouping, density, …) passes straight through to the underlying grid.
 */
export function StackletGrid({
  search,
  onSearchChange,
  searchPlaceholder = "Search...",
  searchAriaLabel = "Search",
  pagination,
  customInline,
  transparentSearch = false,
  slots,
  slotProps,
  // Defaults rather than hardcoded, so a caller can still turn the column
  // rules off (a grid of one wide column reads better without them).
  showCellVerticalBorder = true,
  showColumnVerticalBorder = true,
  ...rest
}: Props) {
  const searchField = onSearchChange ? (
    <SearchField
      aria-label={searchAriaLabel}
      // The wrapper div carries the box; drop it so search blends into the toolbar.
      className={
        transparentSearch
          ? "[&>div]:!border-transparent [&>div]:!bg-transparent [&>div]:hover:!ring-0"
          : undefined
      }
      inputProps={{ placeholder: searchPlaceholder }}
      onChange={onSearchChange}
      value={search ?? ""}
    />
  ) : null;

  // The toolbar carries the design's action-bar chrome by default; callers can
  // extend (not replace) it via slotProps.toolbar.className.
  const mergedSlotProps = {
    ...slotProps,
    toolbar: {
      ...slotProps?.toolbar,
      className: cn(ACTION_BAR_CLASSES, slotProps?.toolbar?.className),
    },
  };

  return (
    <div className="flex flex-col">
      <XGridTable
        {...rest}
        className={cn(GRID_CHROME_CLASSES, rest.className)}
        customInline={customInline ?? searchField}
        showCellVerticalBorder={showCellVerticalBorder}
        showColumnVerticalBorder={showColumnVerticalBorder}
        slotProps={mergedSlotProps}
        slots={{ footer: () => null, ...slots }}
      />
      {pagination ? (
        // The box's own bottom edge is the footer's top rule, so the footer
        // closes the remaining three sides. The border lives here rather than
        // on the component because a grid can also render a footer per
        // expanded section, mid-table, where an outer box would be a stray
        // rule across the rows.
        <div className="border-b border-l border-r border-overlay-medium">
          <PaginationFooter {...pagination} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * The cursor-pagination footer: a page-size dropdown, a 1-based range summary
 * and prev/next controls, divided into three cells. StackletGrid renders it
 * under the grid, inside the border that closes off the block; also exported
 * so a grid can render one per section (e.g. a member list per expanded
 * group), where it is unboxed and sits among the rows.
 */
export function PaginationFooter({
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageSizeChange,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  rangeStart,
  rangeEnd,
  total,
  unit = "item",
  isDisabled = false,
}: StackletGridPagination) {
  return (
    <div className="flex w-full items-stretch text-label-small text-text-secondary">
      <div className="flex items-center gap-sm px-xl py-md">
        <SingleSelectDropdown
          AnchorEle={
            <Button
              IconRight={<ChevronDownIcon size={12} />}
              aria-label={`${capitalize(pluralize(unit))} per page`}
              variant="tertiary"
            >
              {pageSize}
            </Button>
          }
          placement="top"
        >
          {pageSizeOptions.map((size) => (
            <SingleSelectDropdownItem
              isSelected={size === pageSize}
              key={size}
              onPress={() => onPageSizeChange(size)}
            >
              {size}
            </SingleSelectDropdownItem>
          ))}
        </SingleSelectDropdown>
        <span>{pluralize(unit)} per page</span>
      </div>

      <Divider className="self-stretch" orientation="vertical" />

      <div className="flex flex-1 items-center px-xl py-md">
        {total != null ? (
          <span>
            {rangeStart}&ndash;{rangeEnd} of {total} {pluralize(unit, total)}
          </span>
        ) : null}
      </div>

      <Divider className="self-stretch" orientation="vertical" />

      <div className="flex items-center gap-xs px-xl py-md">
        <Button
          IconLeft={<CaretRightIcon className="rotate-180" size={16} />}
          aria-label="Previous page"
          isDisabled={!hasPreviousPage || isDisabled}
          onPress={onPreviousPage}
          variant="tertiary"
        />
        <Button
          IconLeft={<CaretRightIcon size={16} />}
          aria-label="Next page"
          isDisabled={!hasNextPage || isDisabled}
          onPress={onNextPage}
          variant="tertiary"
        />
      </div>
    </div>
  );
}
